/* Reliq • Open sales landing page, round 3.
   - Films: nothing downloads until a film nears the viewport; the wide or tall cut is picked for the screen;
     a film plays only while it is on screen (and, in the instruments stage, only while its page is active).
   - Instruments: on wide screens one sticky stage with a full colour layer per page; the active layer fades in
     whole. On phones each film moves into its own colour section.
   - 3D: the model module (reliq-landing-3d.js) is imported only when its section is about 1.5 screens away.
     Without WebGL, or with reduced motion, the section shows the captured film instead.
   - The way to the buy box: "Get yours" and the phone dock land on the store's own buy box with Add to cart in
     reach; the dock hides only while the hero button or Add to cart is on screen.
   - dataLayer events (rl_*), each with the ad key, for every step from the hero to Add to cart. */
(() => {
  'use strict';
  const root = document.getElementById('rl');
  if (!root) return;

  const RM = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const WIDE = matchMedia('(min-width: 900px)');
  // Data Saver or a 2G-class connection: the films stay on their posters (every poster is the film's first frame)
  const LITE = !!(navigator.connection && (navigator.connection.saveData || /(^|-)2g$/.test(navigator.connection.effectiveType || '')));
  const HDR = 90; // the site header (fixed)
  const AD = root.dataset.ad || '';
  const track = (name, extra) => { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'rl_' + name, rl_ad: AD }, extra || {})); };
  const once = new Set();
  const trackOnce = (name, extra) => { const k = name + JSON.stringify(extra || {}); if (!once.has(k)) { once.add(k); track(name, extra); } };
  const store = document.querySelector('[id$="__reliq_store"]');
  const faq = document.querySelector('[id$="__reliq_store_faq"]');
  const retailers = document.querySelector('[id$="__reliq_retailers"]');

  /* ------------------------------------------------------------------ films */
  const films = new Map(); // el -> state
  function filmState(el) {
    let s = films.get(el);
    if (!s) { s = { el, v: el.querySelector('video'), src: '', near: false, seen: false, gate: true }; films.set(el, s); }
    return s;
  }
  function load(s) {
    const want = WIDE.matches ? s.el.dataset.wide : s.el.dataset.tall;
    if (s.src === want) return;
    s.src = want; s.v.src = want; s.v.load();
  }
  function sync(s) {
    const go = s.near && s.seen && s.gate && !RM && !LITE && !document.hidden;
    if (go) { load(s); const p = s.v.play(); if (p) p.catch(() => {}); }
    else if (!s.v.paused) s.v.pause();
  }
  const nearIO = new IntersectionObserver((es) => es.forEach((e) => { const s = filmState(e.target); s.near = e.isIntersecting; if (s.near && s.gate && !LITE) load(s); sync(s); }), { rootMargin: '900px 0px' });
  const seenIO = new IntersectionObserver((es) => es.forEach((e) => { const s = filmState(e.target); s.seen = e.isIntersecting; sync(s); }), { threshold: 0.25 });
  function initFilms() {
    root.querySelectorAll('[data-rl-film]').forEach((el) => {
      const s = filmState(el);
      s.v.addEventListener('playing', () => el.classList.add('is-playing'));
      // Films wait for the load event: the posters (the hero's is the LCP image) come first, and only then do the
      // observers start downloads, or a film within reach of the first screen would compete with the hero.
      const watch = () => { if (el.hasAttribute('data-eager')) { s.near = true; if (!LITE) load(s); sync(s); } nearIO.observe(el); seenIO.observe(el); };
      document.readyState === 'complete' ? watch() : addEventListener('load', watch, { once: true });
    });
    WIDE.addEventListener('change', () => films.forEach((s) => { if (s.src) { s.src = ''; if (s.gate && !LITE) load(s); sync(s); } }));
    document.addEventListener('visibilitychange', () => films.forEach(sync));
  }
  const gate = (el, on) => { const s = filmState(el); s.gate = on; sync(s); };

  /* ------------------------------------------------------------------ instruments */
  function instruments() {
    const sec = root.querySelector('[data-rl-inst]'); if (!sec) return;
    // The instrument of the ad that was clicked comes first.
    const lead = AD && sec.querySelector(`.rl-panel[data-k="${AD}"]`);
    if (lead) lead.parentNode.prepend(lead);
    // Every film lives in its own panel. On desktop the CSS makes the film sticky across the whole section and the panel
    // clips it, so the colour edge between pages is moved by the browser's own scrolling, with no script.
    const panels = [...sec.querySelectorAll('.rl-panel')];
    // On desktop the sticky films sit on screen for the whole section, so the panels decide instead: a film loads when
    // its panel is near and plays only while its panel is on screen.
    const film = (p) => p.querySelector('.rl-inst__film');
    const nearP = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting && !LITE) load(filmState(film(e.target))); }), { rootMargin: '900px 0px' });
    const onP = new IntersectionObserver((es) => es.forEach((e) => gate(film(e.target), e.isIntersecting)));
    panels.forEach((p) => { if (film(p)) gate(film(p), false); });
    const watch = () => panels.forEach((p) => { if (film(p)) { nearP.observe(p); onP.observe(p); } });
    document.readyState === 'complete' ? watch() : addEventListener('load', watch, { once: true });
    // Count a page as seen when its panel crosses the middle of the screen.
    const mid = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) trackOnce('instrument_view', { page: e.target.dataset.k }); }), { rootMargin: '-50% 0px -50% 0px' });
    panels.forEach((p) => mid.observe(p));
  }

  /* ------------------------------------------------------------------ 3D: take it apart */
  function inside() {
    const sec = root.querySelector('[data-rl-x]'); if (!sec) return;
    const view = sec.querySelector('[data-rl-3d]'), film = sec.querySelector('.rl-x__film');
    if (film) gate(film, false);
    const fallback = (why) => {
      sec.setAttribute('data-fallback', why);
      if (!sec.hasAttribute('data-rl3d')) sec.setAttribute('data-rl3d', 'fallback');
      if (film) gate(film, true);
    };
    const webgl = (() => { try { const c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')); } catch (e) { return false; } })();
    if (RM) return fallback('reduced-motion');
    if (!webgl) return fallback('no-webgl');
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      // The module starts itself on every [data-rl-3d] once imported.
      import(root.dataset.xSrc).catch(() => fallback('load-error'));
    }, { rootMargin: '150% 0px' });
    io.observe(sec);
    // The module marks the section data-rl3d="fallback" when it gives up (weak device, lost context, too slow); follow it.
    const t0 = performance.now();
    new MutationObserver(() => {
      const st = sec.getAttribute('data-rl3d');
      if (st === 'ready') trackOnce('model_ready', { ms: Math.round(performance.now() - t0) });
      if (st === 'fallback') trackOnce('model_fallback', { reason: sec.getAttribute('data-rl3d-reason') || sec.getAttribute('data-fallback') || '' });
      if (st === 'fallback' && !sec.hasAttribute('data-fallback')) fallback(sec.getAttribute('data-rl3d-reason') || 'module');
    }).observe(sec, { attributes: true, attributeFilter: ['data-rl3d'] });
    let dragged = false;
    view.addEventListener('pointerdown', () => { if (!dragged) { dragged = true; track('model_drag'); } });
  }

  /* ------------------------------------------------------------------ YouTube: a facade until played */
  function youtube() {
    root.querySelectorAll('[data-yt]').forEach((btn) => btn.addEventListener('click', () => {
      const f = document.createElement('iframe');
      f.src = `https://www.youtube-nocookie.com/embed/${btn.dataset.yt}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
      f.title = btn.getAttribute('aria-label'); f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen'; f.allowFullscreen = true;
      btn.replaceWith(f);
      track('video_play', { id: btn.dataset.yt });
    }));
  }

  /* ------------------------------------------------------------------ the way to the buy box */
  const buyBtn = () => store && store.querySelector('.product-form__submit');
  function buyY() {
    // Land on a clean edge: the top of the product (photos and buy box) under the header when Add to cart still
    // fits on screen, else the gallery dots (phones) or the product title, else just bring Add to cart up.
    if (!store) return null;
    const g = store.querySelector('.reliq-featured-product__grid') || store, b = buyBtn();
    const dots = store.querySelector('.reliq-dots'), t = dots && dots.offsetHeight ? dots : store.querySelector('.reliq-product-title');
    const at = (el) => el.getBoundingClientRect().top + scrollY - HDR - 16;
    const fits = (y) => !b || b.getBoundingClientRect().bottom + scrollY - y <= innerHeight - 24;
    const y = fits(at(g)) ? at(g) : t && fits(at(t)) ? at(t) : b.getBoundingClientRect().bottom + scrollY - innerHeight + 24;
    return Math.max(0, y);
  }
  // A straight jump, no animated scroll (founder, 2026-10-18).
  let landed = -1;
  const toBuy = () => { const y = buyY(); if (y !== null) { scrollTo({ top: y, behavior: 'instant' }); landed = scrollY; } };
  // Anything above the store that finishes loading after the jump could move it: re-align, again without animation,
  // but never once the visitor has scrolled on.
  const toBuySettled = () => { toBuy(); [150, 600, 1500].forEach((ms) => setTimeout(() => { const y = buyY(); if (y !== null && Math.abs(scrollY - landed) < 2 && Math.abs(y - scrollY) > 8) toBuy(); }, ms)); };
  function buyPath() {
    root.querySelectorAll('a[href="#buy"]').forEach((a) => a.addEventListener('click', (e) => { if (!store) return; e.preventDefault(); toBuySettled(); }));
    // Links to an FAQ answer open it and bring it into view.
    if (faq) faq.insertAdjacentHTML('afterbegin', '<span id="faq" style="display:block;scroll-margin-top:' + HDR + 'px"></span>');
    root.querySelectorAll('[data-rl-faq]').forEach((a) => a.addEventListener('click', (e) => {
      const d = faq && [...faq.querySelectorAll('details')].find((x) => x.querySelector('summary').textContent.trim().startsWith(a.dataset.rlFaq));
      if (!d) return;
      e.preventDefault(); d.open = true;
      scrollTo({ top: d.getBoundingClientRect().top + scrollY - HDR - 16, behavior: RM ? 'auto' : 'smooth' });
    }));
    // The dock hides while any on-page "Get yours" (the hero's or a repeat between sections), Add to cart or the
    // newsletter sign-up at the bottom (#waitlist, so it never sits over the form) is on screen.
    const dock = root.querySelector('[data-rl-dock]'), ctas = [...root.querySelectorAll('.rl-hero__cta, .rl-cta .rl-btn')], b = buyBtn();
    if (dock && ctas.length) {
      const shown = new Set(ctas.slice(0, 1));
      const upd = () => { const on = shown.size === 0; dock.classList.toggle('is-on', on); dock.setAttribute('aria-hidden', String(!on)); dock.querySelector('a').tabIndex = on ? 0 : -1; };
      const io = new IntersectionObserver((es) => { es.forEach((e) => (e.isIntersecting ? shown.add(e.target) : shown.delete(e.target))); upd(); });
      ctas.forEach((c) => io.observe(c));
      if (b) io.observe(b);
      const loop = document.getElementById('waitlist'); if (loop) io.observe(loop);
    }
  }

  /* ------------------------------------------------------------------ events */
  function events() {
    root.querySelectorAll('[data-rl-track]').forEach((a) => a.addEventListener('click', () => track('click', { id: a.dataset.rlTrack })));
    const hero = root.querySelector('.rl-hero video');
    if (hero) hero.addEventListener('playing', () => trackOnce('hero_film_play'), { once: true });
    // First view of each part of the page, the store's sections included.
    const parts = [...root.querySelectorAll(':scope > section')].map((el) => [el.id || el.className.split(' ')[0], el]);
    [['store', store], ['faq', faq], ['retailers', retailers]].forEach((x) => { if (x[1]) parts.push(x); });
    const seen = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { const p = parts.find((x) => x[1] === e.target); seen.unobserve(e.target); trackOnce('section_view', { section: p[0] }); if (p[0] === 'store') track('store_reached'); } }), { threshold: 0.2 });
    parts.forEach((x) => seen.observe(x[1]));
    if (store) {
      store.addEventListener('change', (e) => { if (e.target.type === 'radio') track('variant', { value: e.target.value }); });
      store.addEventListener('click', (e) => { if (e.target.closest('.product-form__submit')) track('add_to_cart'); });
    }
    if (faq) faq.querySelectorAll('details').forEach((d) => d.addEventListener('toggle', () => { if (d.open) track('faq_open', { q: d.querySelector('summary').textContent.trim() }); }));
    if (retailers) retailers.addEventListener('click', (e) => { const a = e.target.closest('a'); if (a) track('retailer_click', { href: a.href }); });
  }

  const init = () => { initFilms(); instruments(); inside(); youtube(); buyPath(); events(); };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
